import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreateRoomCategoryRequest,
  UpdateRoomCategoryRequest,
  RoomCategoryFilters,
} from "@/types/roomCategory";
import { roomCategoryService } from "@/services/roomCategoryService";

// queryKeys/roomCategoryKeys.ts
export const roomCategoryKeys = {
  all: ["roomCategories"] as const,

  // All list queries
  lists: () => [...roomCategoryKeys.all, "list"] as const,

  // Specific list query based on hotel + pagination + filters
  list: (
    hotelId: string,
    page: number,
    limit: number,
    filters?: RoomCategoryFilters,
  ) =>
    [...roomCategoryKeys.lists(), hotelId, page, limit, filters ?? {}] as const,

  // All detail queries
  details: () => [...roomCategoryKeys.all, "detail"] as const,

  // Specific category detail
  detail: (id: string) => [...roomCategoryKeys.details(), id] as const,

  // Hotel-specific categories (if needed separately)
  byHotel: (hotelId: string) =>
    [...roomCategoryKeys.all, "byHotel", hotelId] as const,

  // Filters settings (must be distinct from byHotel!)
  filtersSettings: (hotelId: string) =>
    [...roomCategoryKeys.all, "filtersSettings", hotelId] as const,
};

// Get paginated + filtered list
export const useRoomCategories = (
  hotelId: string,
  page: number = 1,
  limit: number = 10,
  filters?: RoomCategoryFilters,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: roomCategoryKeys.list(hotelId, page, limit, filters),
    queryFn: () =>
      roomCategoryService.getRoomCategoriesByHotel(
        hotelId,
        page,
        limit,
        filters,
      ),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && Boolean(hotelId),
  });
};

// Get single category
export const useRoomCategory = (id: string, hotelId: string) => {
  return useQuery({
    queryKey: roomCategoryKeys.detail(id),
    queryFn: () => roomCategoryService.getRoomCategoryById(id, hotelId),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// Create
export const useCreateRoomCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateRoomCategoryRequest) =>
      roomCategoryService.createRoomCategory(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roomCategoryKeys.lists() });
    },
  });
};

// Update
export const useUpdateRoomCategory = (id: string, hotelId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateRoomCategoryRequest) =>
      roomCategoryService.updateRoomCategory(id, hotelId, request),
    onSuccess: (updatedCategory) => {
      queryClient.setQueryData(
        roomCategoryKeys.detail(updatedCategory.id),
        updatedCategory,
      );
      queryClient.invalidateQueries({ queryKey: roomCategoryKeys.lists() });
    },
  });
};

// Delete
export const useDeleteRoomCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, hotelId }: { id: string; hotelId: string }) =>
      roomCategoryService.deleteRoomCategory(id, hotelId),
    onSuccess: (_, { id }) => {
      queryClient.removeQueries({
        queryKey: roomCategoryKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: roomCategoryKeys.lists() });
    },
  });
};

// Get filters settings
export const useRoomCategoriesFiltersSettings = (hotelId: string) => {
  return useQuery({
    queryKey: roomCategoryKeys.filtersSettings(hotelId),
    queryFn: () => roomCategoryService.getFiltersSettings(hotelId),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(hotelId),
  });
};
